/** @format */

import { Grid, Tooltip } from '@mui/material';
import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from 'src/components';
import { CustomDropDown } from 'src/components/CustomDropDown/CustomDropDown';
import LanguageSelectDropdown from 'src/components/LanguageSelectDropdown';
import { ArrowDownIcon, EditIcon, HideIcon, PlusIcon, ThreedotsIcon, ViewIcon } from 'src/components/icons';
import Breadcrumb from 'src/components/Breadcrumb';
import Page from 'src/components/Page';
import { getTopicTree, togglePublishTopic, setSelectedLanguage } from 'src/slices/topics';
import { getDefaultSearchLanguage } from 'src/slices/search';
import { useDispatch, useSelector } from 'src/store';
import { flattenTopicTreeForAdmin, formatAdminBrandLabel } from 'src/utils/administration';
import { getExtranetFilterLanguageItems } from 'src/utils/extranetLanguages';
import { startViewTransition } from 'src/utils/viewTransition';
import AdministrationActionConfirmDialog from './AdministrationActionConfirmDialog';
import TopicsAdminPageSkeleton from './TopicsAdminPageSkeleton';

export default function TopicsAdminPage() {
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { topicTree, topicTreeLoading, togglePublishTopicLoading, selectedLanguage } = useSelector(
    state => state.topics
  );

  const topicsList = useMemo(() => flattenTopicTreeForAdmin(topicTree), [topicTree]);
  const [displayedRows, setDisplayedRows] = useState(topicsList);

  const languageItems = useMemo(() => getExtranetFilterLanguageItems(t), [t]);

  const formatLabel = (selectedKey, items, fallback) => {
    if (!selectedKey) return fallback;
    const selected = items.find(item => item.key === selectedKey || item.value === selectedKey);
    return selected ? selected.label : fallback;
  };

  const selectedLanguageLabel = useMemo(
    () => formatLabel(selectedLanguage, languageItems, 'Select Language'),
    [selectedLanguage, languageItems]
  );
  const [expandedRowKeys, setExpandedRowKeys] = useState({});
  const [actionsDropdownOpenKey, setActionsDropdownOpenKey] = useState(null);
  const [togglePublishModalTopic, setTogglePublishModalTopic] = useState(null);
  const [togglePublishDialogError, setTogglePublishDialogError] = useState('');
  const prevTopicTreeLoadingRef = useRef(topicTreeLoading);

  useEffect(() => {
    const lang = selectedLanguage || (typeof i18n.language === 'string' && i18n.language.trim() ? i18n.language : 'en');
    dispatch(getTopicTree());
  }, [dispatch, i18n.language, selectedLanguage]);

  useEffect(() => {
    const wasLoading = prevTopicTreeLoadingRef.current;
    prevTopicTreeLoadingRef.current = topicTreeLoading;

    if (wasLoading && !topicTreeLoading) {
      startViewTransition(() => {
        setDisplayedRows(topicsList);
      });
    } else if (topicTreeLoading) {
      setDisplayedRows(prevRows => (Array.isArray(prevRows) && prevRows.length > 0 ? prevRows : topicsList));
    } else if (!topicTreeLoading) {
      setDisplayedRows(topicsList);
    }
  }, [topicTreeLoading, topicsList]);

  useEffect(() => {
    setExpandedRowKeys({});
  }, [topicTree]);

  const hasDisplayedRows = Array.isArray(displayedRows) && displayedRows.length > 0;
  const shouldShowSkeleton = (topicTreeLoading || displayedRows === null) && !hasDisplayedRows;
  const shouldDisableList = topicTreeLoading && hasDisplayedRows;
  const shouldShowEmpty =
    !shouldShowSkeleton && !topicTreeLoading && Array.isArray(displayedRows) && displayedRows.length === 0;

  const visibleRows = useMemo(() => {
    if (!Array.isArray(displayedRows)) {
      return [];
    }

    const visibleKeys = new Set();
    return displayedRows.filter(item => {
      const depth = Number(item?.depth) || 0;
      const rowKey = String(item?.key || '');
      if (!rowKey) {
        return false;
      }

      if (depth === 0) {
        visibleKeys.add(rowKey);
        return true;
      }

      const parentKey = rowKey.includes('-') ? rowKey.slice(0, rowKey.lastIndexOf('-')) : '';
      const shouldShow = Boolean(parentKey && visibleKeys.has(parentKey) && expandedRowKeys[parentKey]);
      if (shouldShow) {
        visibleKeys.add(rowKey);
      }
      return shouldShow;
    });
  }, [displayedRows, expandedRowKeys]);

  const handleToggleChildren = topicKey => {
    setExpandedRowKeys(prev => {
      if (prev[topicKey]) {
        const next = { ...prev };
        Object.keys(next).forEach(key => {
          if (key === topicKey || key.startsWith(`${topicKey}-`)) {
            delete next[key];
          }
        });
        return next;
      }
      return { ...prev, [topicKey]: true };
    });
  };

  const handleActionsMenuClick = (key, topic) => {
    setActionsDropdownOpenKey(null);
    if (key === 'edit' && topic?.id !== undefined && topic?.id !== null) {
      navigate(`/administration/topics/${topic.id}/${selectedLanguage}`);
      return;
    }
    if (key === 'toggle-publish') {
      setTogglePublishDialogError('');
      setTogglePublishModalTopic(topic);
    }
  };

  const handleTogglePublishModalClose = () => {
    if (togglePublishTopicLoading) return;
    setTogglePublishDialogError('');
    setTogglePublishModalTopic(null);
  };

  const handleTogglePublishConfirm = async () => {
    if (!togglePublishModalTopic) return;
    const topicToToggle = togglePublishModalTopic;
    setTogglePublishDialogError('');
    try {
      await dispatch(togglePublishTopic(topicToToggle));
      setTogglePublishModalTopic(null);
      const lang = typeof i18n.language === 'string' && i18n.language.trim() ? i18n.language : 'en';
      dispatch(getTopicTree());
    } catch (error) {
      setTogglePublishModalTopic(topicToToggle);
      setTogglePublishDialogError(t('common.unknownError'));
    }
  };

  const handleCreateTopicNavigate = () => {
    navigate('/administration/topics/create');
  };

  const handleDuplicateTopicNavigate = topic => {
    navigate('/administration/topics/create', { state: { duplicateTopic: topic } });
  };

  return (
    <Page className="administration-topics-page" title={t('administrationPage.topicsTitle')}>
      <Breadcrumb data={[{ title: t('administrationPage.topicsTitle') }]} />
      <Grid className="administration-topics-page__container container">
        <div className="administration-topics-page__toolbar">
          <h1 className="administration-topics-page__heading">{t('administrationPage.topicsTitle')}</h1>
          <div className="administration-topics-page__toolbar-actions">
            <LanguageSelectDropdown
              items={languageItems}
              selectedKey={selectedLanguage}
              label={selectedLanguageLabel}
              defaultValue={getDefaultSearchLanguage()}
              onReset={value => dispatch(setSelectedLanguage(value))}
              resetAriaLabel={t('common.button.resetToDefault')}
              onSelect={value => dispatch(setSelectedLanguage(value))}
            />
            <Button
              type="button"
              label={t('administrationPage.addTopic')}
              additionalClassName="administration-topics-page__add-btn"
              onClick={handleCreateTopicNavigate}
            />
          </div>
        </div>

        {shouldShowSkeleton ? (
          <TopicsAdminPageSkeleton />
        ) : shouldShowEmpty ? (
          <div className="administration-topics-page__scroll">
            <div className="administration-topics-page__sheet administration-topics-page__sheet--vt">
              <p className="administration-topics-page__empty">{t('administrationPage.topicsEmpty')}</p>
            </div>
          </div>
        ) : (
          <div className="administration-topics-page__scroll">
            <div
              className={`administration-topics-page__sheet administration-topics-page__sheet--vt${
                shouldDisableList ? ' administration-topics-page__sheet--loading' : ''
              }`}
              role="table"
              aria-label={t('administrationPage.topicsTitle')}
              aria-busy={shouldDisableList}>
              <div className="administration-topics-page__mobile-labels" aria-hidden="true">
                <span className="administration-topics-page__mobile-label administration-topics-page__mobile-label--primary">
                  {t('administrationPage.columnName')}
                </span>
                <span className="administration-topics-page__mobile-label administration-topics-page__mobile-label--secondary">
                  {t('administrationPage.columnBrand')}
                </span>
              </div>
              <div className="administration-topics-page__header-group" role="rowgroup">
                <div className="administration-topics-page__header-row" role="row">
                  <div className="administration-topics-page__header-cell" role="columnheader">
                    {t('administrationPage.columnName')}
                  </div>
                  <div className="administration-topics-page__header-cell" role="columnheader">
                    {t('administrationPage.columnDisplayName')}
                  </div>
                  <div className="administration-topics-page__header-cell" role="columnheader">
                    {t('administrationPage.columnBrand')}
                  </div>
                  <div
                    className="administration-topics-page__header-cell administration-topics-page__header-cell--actions"
                    role="columnheader">
                    <span className="visually-hidden">{t('administrationPage.columnActions')}</span>
                  </div>
                </div>
              </div>
              <div className="administration-topics-page__body-group" role="rowgroup">
                {(visibleRows || []).map(item => {
                  const { key: topicKey, name, displayName, brand: brandKey, depth = 0, topic } = item;
                  const vtName = `adm-t-${String(topicKey).replace(/[^a-zA-Z0-9_-]/g, '-')}`;
                  const isPublished = Boolean(topic?.published);
                  const hasChildren = Boolean(Array.isArray(topic?.children) && topic.children.length > 0);
                  const isExpanded = Boolean(expandedRowKeys[topicKey]);
                  return (
                    <div
                      className={`administration-topics-page__data-row${
                        actionsDropdownOpenKey === topicKey ? ' administration-topics-page__data-row--actions-open' : ''
                      }${!isPublished ? ' administration-topics-page__data-row--unpublished' : ''}`}
                      role="row"
                      key={topicKey}
                      style={
                        {
                          viewTransitionName: vtName,
                          '--topic-depth': depth
                        } as CSSProperties
                      }>
                      <div
                        className="administration-topics-page__cell administration-topics-page__cell--name"
                        role="cell">
                        {name}
                      </div>
                      <div
                        className="administration-topics-page__cell administration-topics-page__cell--display"
                        role="cell">
                        {displayName}
                      </div>
                      <div
                        className="administration-topics-page__cell administration-topics-page__cell--brand"
                        role="cell">
                        {formatAdminBrandLabel(brandKey)}
                      </div>
                      <div
                        className="administration-topics-page__cell administration-topics-page__cell--actions"
                        role="cell">
                        <div className="administration-topics-page__row-actions">
                          <Tooltip
                            title={t('administrationPage.tooltipDuplicate')}
                            placement="top"
                            arrow
                            classes={{ tooltip: 'administration-topics-page__tooltip' }}>
                            <button
                              type="button"
                              className="administration-topics-page__icon-btn"
                              aria-label={t('administrationPage.tooltipDuplicate')}
                              onClick={() => handleDuplicateTopicNavigate(topic)}>
                              <PlusIcon />
                            </button>
                          </Tooltip>
                          <Tooltip
                            title={t('administrationPage.tooltipEditDelete')}
                            placement="top"
                            arrow
                            disableHoverListener={actionsDropdownOpenKey === topicKey}
                            disableFocusListener={actionsDropdownOpenKey === topicKey}
                            disableTouchListener={actionsDropdownOpenKey === topicKey}
                            classes={{ tooltip: 'administration-topics-page__tooltip' }}>
                            <span className="administration-topics-page__tooltip-dropdown-wrap">
                              <CustomDropDown
                                placement="bottomRight"
                                overlayClassName="administration-topics-page__actions-dropdown"
                                open={actionsDropdownOpenKey === topicKey}
                                onOpenChange={open => setActionsDropdownOpenKey(open ? topicKey : null)}
                                trigger={
                                  <button
                                    type="button"
                                    className="administration-topics-page__icon-btn"
                                    aria-label={t('administrationPage.tooltipEditDelete')}
                                    aria-haspopup="menu">
                                    <ThreedotsIcon />
                                  </button>
                                }>
                                <ul className="custom-dropdown__menu" role="menu">
                                  <li className="custom-dropdown__menu-item" role="none">
                                    <button
                                      type="button"
                                      role="menuitem"
                                      className="administration-topics-page__actions-menu-item"
                                      onClick={() => handleActionsMenuClick('edit', topic)}>
                                      <span className="dropdown-item-with-icon">
                                        <EditIcon width={20} height={20} />
                                        {t('administrationPage.actionEditTopic')}
                                      </span>
                                    </button>
                                  </li>
                                  <li className="custom-dropdown__menu-item" role="none">
                                    <button
                                      type="button"
                                      role="menuitem"
                                      className="administration-topics-page__actions-menu-item"
                                      onClick={() => handleActionsMenuClick('toggle-publish', topic)}>
                                      <span className="dropdown-item-with-icon">
                                        {isPublished ? (
                                          <>
                                            <HideIcon width={20} height={20} />
                                            {t('administrationPage.actionUnpublish')}
                                          </>
                                        ) : (
                                          <>
                                            <ViewIcon width={20} height={20} />
                                            {t('administrationPage.actionPublish')}
                                          </>
                                        )}
                                      </span>
                                    </button>
                                  </li>
                                </ul>
                              </CustomDropDown>
                            </span>
                          </Tooltip>
                          {hasChildren ? (
                            <Tooltip
                              title={
                                isExpanded
                                  ? t('administrationPage.tooltipCollapse')
                                  : t('administrationPage.tooltipExpand')
                              }
                              placement="top"
                              arrow
                              classes={{ tooltip: 'administration-topics-page__tooltip' }}>
                              <button
                                type="button"
                                className={`administration-topics-page__toggle-children-btn${
                                  isExpanded ? ' administration-topics-page__toggle-children-btn--expanded' : ''
                                }`}
                                aria-label={
                                  isExpanded
                                    ? t('administrationPage.tooltipCollapse')
                                    : t('administrationPage.tooltipExpand')
                                }
                                onClick={() => handleToggleChildren(topicKey)}>
                                <ArrowDownIcon />
                              </button>
                            </Tooltip>
                          ) : (
                            <span className="administration-topics-page__toggle-children-spacer" aria-hidden="true" />
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </Grid>
      <AdministrationActionConfirmDialog
        open={Boolean(togglePublishModalTopic)}
        onClose={handleTogglePublishModalClose}
        onConfirm={handleTogglePublishConfirm}
        message={t('administrationPage.confirmTogglePublishQuestion')}
        cancelLabel={t('administrationPage.confirmNo')}
        confirmLabel={t('administrationPage.confirmYes')}
        error={togglePublishDialogError || undefined}
        confirmDisabled={togglePublishTopicLoading}
        title={t('administrationPage.confirmTogglePublishTitle')}
      />
    </Page>
  );
}
